import { JSX } from "react";
import { ProductTypeEnum } from "../../enums/ProductTypeEnum";
import { LanguageInterface } from "../../interfaces/shared/LanguageInterface";
import { ProductDetailInterface } from "../../interfaces/apiV2/product/ProductDetailInterface";
import { ProductIndexItemInterface } from "../../interfaces/apiV2/shared/ProductIndexItemInterface";
import {
    ProductAddOnCardInterface,
    ProductAssociationCardInterface,
} from "../../interfaces/apiV2/product/ProductAssociatedInterface";
import { ProductOlderModelAssociationInterface } from "../../interfaces/apiV2/product/ProductAssociatedInterface";
import { ProductGroupedDownloadItemInterface } from "../../interfaces/apiV2/product/ProductDownloadsInterface";
import {
    showDownloadsSection,
    showOlderModelsSection,
    showProductSlidersSection,
    showSpecificationInfoSection,
} from "../../utils/productDetail/productDetailSectionVisibility";
import { ProductGeneralInformation } from "./ProductGeneralInformation";
import { ProductSpecifications } from "./ProductSpecifications";
import { ProductDownloads } from "./ProductDownloads";
import { RelatedProducts } from "./RelatedProducts";
import { ProductOlderModels } from "./ProductOlderModels";
import { useStringTranslation } from "../../hooks/useStringTranslation";

interface Props {
    data: ProductDetailInterface | undefined;
    isPageLoading: boolean;
    translationsLoaded: boolean;
    activeLanguage: LanguageInterface | null;
    accessories: ProductIndexItemInterface[];
    similarProducts: ProductAssociationCardInterface[];
    addOns: ProductAddOnCardInterface[];
    olderModels: ProductOlderModelAssociationInterface[];
    isAssociationsLoading: boolean;
    marketingDownloads: Record<string, ProductGroupedDownloadItemInterface[]>;
    technicalDownloads: Record<string, ProductGroupedDownloadItemInterface[]>;
    marketingDownloadsCount: number;
    technicalDownloadsCount: number;
    downloadsLoading: boolean;
}

export const ProductDetailsBody = ({
    data,
    isPageLoading,
    translationsLoaded,
    activeLanguage,
    accessories,
    similarProducts,
    addOns,
    olderModels,
    isAssociationsLoading,
    marketingDownloads,
    technicalDownloads,
    marketingDownloadsCount,
    technicalDownloadsCount,
    downloadsLoading,
}: Props): JSX.Element => {
    const { t } = useStringTranslation();

    const showSpecificationInfo = showSpecificationInfoSection(
        data,
        isPageLoading,
        translationsLoaded,
    );

    const showProductSliders = showProductSlidersSection(
        isPageLoading,
        translationsLoaded,
        isAssociationsLoading,
        accessories.length,
        similarProducts.length,
        addOns.length,
    );

    const showDownloads = showDownloadsSection(
        isPageLoading,
        downloadsLoading,
        translationsLoaded,
        marketingDownloads,
        technicalDownloads,
        marketingDownloadsCount,
        technicalDownloadsCount,
    );

    const showOlderModels = showOlderModelsSection(
        translationsLoaded,
        olderModels.length,
    );

    const slidersLoading = isPageLoading || isAssociationsLoading;

    return (
        <>
            {showSpecificationInfo && (
                <div className="p-product-details__specification-info">
                    <ProductGeneralInformation
                        data={data}
                        activeLanguage={activeLanguage}
                        loading={isPageLoading}
                    />

                    <ProductSpecifications
                        data={data}
                        activeLanguage={activeLanguage}
                        loading={isPageLoading}
                    />
                </div>
            )}

            {showProductSliders && (
                <div className="p-product-details__product-sliders">
                    <RelatedProducts
                        products={accessories}
                        title={t("accessories")}
                        type={ProductTypeEnum.accessories}
                        loading={slidersLoading}
                    />

                    <RelatedProducts
                        products={similarProducts}
                        title={t("similar-machines")}
                        loading={slidersLoading}
                    />

                    <RelatedProducts
                        products={addOns}
                        title={t("add-ons")}
                        showPrice
                        fullWidth
                        loading={slidersLoading}
                    />
                </div>
            )}

            {showDownloads && (
                <div className="p-product-details__downloads">
                    <ProductDownloads
                        marketingDownloads={marketingDownloads}
                        technicalDownloads={technicalDownloads}
                        marketingDownloadsCount={marketingDownloadsCount}
                        technicalDownloadsCount={technicalDownloadsCount}
                    />
                </div>
            )}

            {showOlderModels && (
                <div className="p-product-details__older-models">
                    <ProductOlderModels
                        items={olderModels}
                        title={t("older-models")}
                    />
                </div>
            )}
        </>
    );
};
